import { AccessTime as ExpectedIcon } from '@mui/icons-material';
import type { ITransferStatus } from '@on3/ui-lib/api/schema/custom-contracts';
import { Check } from '@on3/ui-lib/components/Svg';
import { EnteredIcon } from '@public/components/Icons/EnteredIcon';
import { UndecidedIcon } from '@public/components/Icons/UndecidedIcon';
import { WithdrawnIcon } from '@public/components/Icons/WithdrawnIcon';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { ArrowRight } from '@public/components/Svg/ArrowRight';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useRouter } from 'next/router';

import styles from './TransferStatus.module.scss';

export const TransferStatus = ({
  type,
  date,
  committedAsset,
  committedOrganization,
  lastTeam,
  classRank,
  transferEntered,
  transferredAsset,
  className,
  predictions,
  recruitmentKey,
  slug,
  year,
}: ITransferStatus) => {
  const router = useRouter();
  const { sport } = router.query;

  const hasStatus = type !== 'None' && committedOrganization?.slug;
  const hasPredictions = !!predictions?.length;
  const hasWithdrawn = type === 'Withdrawn';
  const isSigned = type === 'Signed';
  const hasEntered = type === 'Entered';
  const isEnrolled = type === 'Enrolled';
  const isExpectedToEnter = type === 'Expected';

  return (
    <div className={clsx(styles.block, className)}>
      <div className={styles.playerStatusContainer}>
        <div
          className={clsx(styles.playerStatusItem, {
            [styles.expectedStatus]: isExpectedToEnter,
          })}
        >
          {(isEnrolled || isSigned) && (
            <div className={styles.checkmarkContainer}>
              <Check className={styles.checkmarkIcon} />
            </div>
          )}
          {(hasEntered || hasWithdrawn || isExpectedToEnter) && (
            <div className={styles.statusIconContainer}>
              {hasEntered && <EnteredIcon className={styles.statusIcon} />}
              {hasWithdrawn && <WithdrawnIcon className={styles.statusIcon} />}
              {isExpectedToEnter && (
                <ExpectedIcon className={styles.statusIcon} />
              )}
            </div>
          )}
          <span
            className={clsx(styles.statusLabel, {
              [styles.signed]: isEnrolled || isSigned,
              [styles.withdrawn]: hasWithdrawn,
            })}
          >
            {type}
          </span>
        </div>

        {date && (
          <Text className={styles.statusDate}>
            {format(new Date(date), 'M/dd/yyyy')}
          </Text>
        )}
      </div>
      <div className={styles.teamStatusContainer}>
        <div className={styles.lastTeamWrapper}>
          <TextLink
            className={styles.lastTeam}
            href={`/college/${lastTeam?.slug}/${sport}/${year}/transfers/`}
            underline="none"
          >
            <TeamAvatar
              className={styles.teamLogo}
              height={20}
              slug={lastTeam?.name}
              src={lastTeam?.assetUrl?.url}
              width={20}
            />
          </TextLink>
          <ArrowRight className={styles.arrowForward} stroke="#1d252c" />
        </div>
        <div>
          {(hasStatus || hasWithdrawn) && (
            <TextLink
              className={styles.committedLogoContainer}
              href={`/college/${committedOrganization?.slug}/${sport}/${year}/transfers/`}
              underline="none"
            >
              <TeamAvatar
                className={styles.teamLogo}
                height={20}
                slug={committedOrganization?.name}
                src={committedOrganization?.assetUrl}
                width={20}
              />
            </TextLink>
          )}
          {hasEntered && hasPredictions && (
            <TextLink
              className={styles.predictionsContainer}
              href={`/rivals/${slug}/recruiting/${recruitmentKey}/rpm/`}
              underline="none"
            >
              {predictions?.slice(0, 2).map((org) => {
                const orgDistance = org?.distance ?? -1;
                const distanceText =
                  orgDistance > -1 ? Math.round(orgDistance) : '-';

                return (
                  <div className={styles.prediction} key={org.key}>
                    {!!org.percent && <span>{org.percent.toFixed(1)}%</span>}
                    <TeamAvatar
                      className={styles.teamLogo}
                      height={20}
                      slug={org?.organization?.name}
                      src={org?.organization?.assetUrl?.url}
                      width={20}
                    />
                    <span className={styles.rpmDistance}>
                      {distanceText}

                      <span aria-label="Miles">mi</span>
                    </span>
                  </div>
                );
              })}
            </TextLink>
          )}
          {(hasEntered || isExpectedToEnter) && !hasPredictions && (
            <div className={styles.undecidedContainer}>
              <UndecidedIcon className={styles.teamLogo} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
